import React, { useState } from 'react';
import { ChevronDownIcon, CheckCircle2Icon } from 'lucide-react';
import { MoneyInput } from './MoneyInput';
import { formatCurrency } from '../../utils/roadmapCalcs';
import type { Debts } from '../../types/roadmap';
interface DebtsGroupProps {
  debts: Debts;
  onChange: (patch: Partial<Debts>) => void;
}
const ROWS: {
  key: keyof Debts;
  label: string;
  note: string;
  inputLabel: string;
  suffix?: string;
}[] = [{
  key: 'homeLoans',
  label: 'Home loans',
  note: 'The total amount still owing across any property loans.',
  inputLabel: 'Home loan balance'
}, {
  key: 'carPersonalLoans',
  label: 'Car and personal loans',
  note: 'The total balance still owing, not the monthly repayment.',
  inputLabel: 'Car and personal loan balance'
}, {
  key: 'creditCardLimits',
  label: 'Credit cards',
  note: 'Your total card limits — lenders count the limit, not the balance.',
  inputLabel: 'Total credit card limits'
}, {
  key: 'hecs',
  label: 'HECS or HELP',
  note: 'Roughly what is left on your study loan.',
  inputLabel: 'HECS or HELP balance'
}, {
  key: 'buyNowPayLater',
  label: 'Buy Now Pay Later',
  note: 'Anything owing on Afterpay, Zip or similar.',
  inputLabel: 'Buy Now Pay Later balance'
}];

/**
 * Every debt in one screen, each row collapsed by default. The screen opens
 * looking almost empty and only grows for the debts that actually apply — the
 * alternative was five more screens most people would answer with zero.
 */
export function DebtsGroup({
  debts,
  onChange
}: DebtsGroupProps) {
  const [openKey, setOpenKey] = useState<keyof Debts | null>(null);
  return <div className="space-y-3" data-id="element-190" data-mp="422vkaiwz2qc">
      {ROWS.map(row => {
      const isOpen = openKey === row.key;
      const value = debts[row.key];
      const hasValue = value > 0;
      return <div key={row.key} className={`rounded-2xl border overflow-hidden transition-colors ${hasValue ? 'border-primary-500/50 bg-primary-50/50 dark:bg-primary-500/5' : 'border-slate-200 dark:border-white/10 bg-white dark:bg-white/5'}`} data-id="element-191" data-mp="2osbpzxlsp0tw">
            <button type="button" onClick={() => setOpenKey(isOpen ? null : row.key)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left" data-id="element-192" data-mp="liq7r0kf7rp8">
              <span className="flex items-center gap-2.5 min-w-0" data-id="element-193" data-mp="7k0bfn5fdw1x">
                {hasValue && <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 text-primary-500" data-id="element-194" data-mp="2kxhaxqzha8q" />}
                <span className="text-base font-semibold text-slate-900 dark:text-white truncate" data-id="element-195" data-mp="3trgp041iwk2t">
                  {row.label}
                </span>
              </span>

              <span className="flex items-center gap-2 flex-shrink-0" data-id="element-196" data-mp="1wwu8cplike39">
                {hasValue && <span className="text-sm font-medium text-primary-600 dark:text-primary-400" data-id="element-197" data-mp="1ry0xz61ix37b">
                    {formatCurrency(value)}
                  </span>}
                <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} data-id="element-198" data-mp="24gmull4tcazh" />
              </span>
            </button>

            {isOpen && <div className="px-5 pb-5 pt-1" data-id="element-199" data-mp="1hii4ayafzmmx">
                <p className="mb-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-200" data-mp="1hjt7avw2cqcz">
                  {row.note}
                </p>
                <MoneyInput label={row.inputLabel} value={value} autoFocus={false} suffix={row.suffix} onChange={next => onChange({
            [row.key]: next
          } as Partial<Debts>)} data-id="element-201" data-mp="1qiogmguemgki" />
              </div>}
          </div>;
    })}

      <p className="pt-1 text-sm text-slate-500 dark:text-slate-400" data-id="element-202" data-mp="jnpaacb4jck3">
        Skip anything that does not apply to you.
      </p>
    </div>;
}